import { sendFBInitiateCheckout, sendFBPurchase, sendCompleteRegistration } from './api';
import { rinoLogEvent } from './rinoAnalytics';

function generateEventId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'ev-' + Date.now() + '-' + Math.random().toString(36).slice(2);
}

function currentEventSourceUrl() {
    try {
        return typeof window !== 'undefined' ? window.location.href : null;
    } catch (e) {
        return null;
    }
}

let amplitudeInitialized = false;

function doInitAmplitude() {
    if (amplitudeInitialized) return true;
    if (!window.amplitude) return false;

    try {
        if (window.sessionReplay) {
            const sessionReplayTracking = window.sessionReplay.plugin({
                forceSessionTracking: true,
                sampleRate: 0,
            });
            window.amplitude.add(sessionReplayTracking);
        }
        window.amplitude.init("3d5f3f0b291a77a6af86c91549a90826", {
            fetchRemoteConfig: false,
            autocapture: false,
        });
        amplitudeInitialized = true;
        console.log('Amplitude initialized successfully');
        return true;
    } catch (e) {
        console.warn('Amplitude initialization failed', e);
        return false;
    }
}

export function initAmplitude() {
    if (doInitAmplitude()) return;

    console.log('Amplitude not loaded yet, waiting for async script...');
    const start = Date.now();
    const interval = setInterval(() => {
        if (doInitAmplitude()) {
            clearInterval(interval);
        } else if (Date.now() - start > 10000) {
            clearInterval(interval);
            console.warn('Amplitude failed to load within 10s');
        }
    }, 200);
}

function fbq(...args) {
    if (typeof window.fbq === 'function') {
        window.fbq(...args);
    }
}

function amplitudeTrack(event, props) {
    if (window.amplitude) {
        window.amplitude.track(event, props);
        console.log(`📊 Amplitude: ${event}`, props);
    }
}

function dataLayerPush(item) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(item);
    console.log(`📊 DataLayer: ${item.event}`, item);
}

export function trackPageView() {
    fbq('track', 'PageView');
}

export function trackAddToCart() {
    fbq('track', 'AddToCart');
}

export function trackCompleteRegistration(email, fbc) {
    const eventId = generateEventId();
    const eventSourceUrl = currentEventSourceUrl();
    fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
    sendCompleteRegistration(email, fbc, eventId, eventSourceUrl);
}

export function trackScreenPresented({ screenCid, onboardingId }) {
    const props = {
        screen_cid: screenCid,
        funnelID: onboardingId,
        engine: 'rino',
    };
    amplitudeTrack('screen_presented', props);
    rinoLogEvent('screen_presented', props);
    dataLayerPush({ event: 'screen_presented', ...props });
}

export function trackScreenCompleted({ screenCid, onboardingId, reply }) {
    const props = {
        screen_cid: screenCid,
        funnelID: onboardingId,
        engine: 'rino',
        ...(reply ? { reply } : {}),
    };
    amplitudeTrack('screen_completed', props);
    rinoLogEvent('screen_completed', props);
    dataLayerPush({ event: 'screen_completed', ...props });
}

export function trackResetOnFirstScreen({ screenCid, onboardingId }) {
    const props = {
        screen_cid: screenCid,
        funnelID: onboardingId,
        engine: 'rino',
    };
    amplitudeTrack('onb_resetOnFirstScreen', props);
    rinoLogEvent('onb_resetOnFirstScreen', props);
    dataLayerPush({ event: 'onb_resetOnFirstScreen', ...props });
}

export function trackInitiateCheckout(email) {
    const eventId = generateEventId();
    const eventSourceUrl = currentEventSourceUrl();
    fbq('track', 'InitiateCheckout', {}, { eventID: eventId });
    sendFBInitiateCheckout(email, eventId, eventSourceUrl);
}

export function trackPurchaseStarted({ type, onboardingId, priceID, discountID }) {
    const dlItem = {
        event: 'purchase_started',
        type,
        funnelID: onboardingId,
        priceID,
        discountID,
    };
    dataLayerPush(dlItem);
    const ampProps = {
        engine: 'rino',
        type,
        funnelID: onboardingId,
        priceID,
        discountID,
    };
    amplitudeTrack('purchase_started', ampProps);
    rinoLogEvent('purchase_started', ampProps);
}

export function trackPurchaseCompleted({ onboardingId, priceID, discountID, price, email }) {
    const valueUSD = price / 100;
    const eventId = generateEventId();
    const eventSourceUrl = currentEventSourceUrl();

    fbq('track', 'Purchase', { currency: 'USD', value: valueUSD }, { eventID: eventId });
    sendFBPurchase(email, valueUSD, eventId, eventSourceUrl);

    dataLayerPush({
        event: 'purchase_completed_success',
        engine: 'rino',
        funnelID: onboardingId,
        priceID,
        discountID,
        value: valueUSD,
        currency: 'USD',
        email,
    });

    const ampProps = {
        engine: 'rino',
        funnelID: onboardingId,
        priceID,
        discountID,
    };
    amplitudeTrack('purchase_completed_success', ampProps);
    rinoLogEvent('purchase_completed_success', ampProps);
}

export function trackPurchaseFailed({ onboardingId, paymentProvider, data }) {
    dataLayerPush({
        event: 'purchase_failed',
        funnelID: onboardingId,
        paymentProvider: paymentProvider || 'stripe',
        data,
    });
    const ampProps = {
        engine: 'rino',
        funnelID: onboardingId,
        paymentProvider: paymentProvider || 'stripe',
        data,
    };
    amplitudeTrack('purchase_failed', ampProps);
    rinoLogEvent('purchase_failed', ampProps);
}

export function trackPdfPurchaseFailed({ onboardingId, email, paymentProvider, data }) {
    dataLayerPush({
        event: 'pdf_purchase_failed',
        funnelID: onboardingId,
        email,
        paymentProvider: paymentProvider || 'stripe',
        data,
    });
    const ampProps = {
        engine: 'rino',
        funnelID: onboardingId,
        email,
        paymentProvider: paymentProvider || 'stripe',
        data,
    };
    amplitudeTrack('pdf_purchase_failed', ampProps);
    rinoLogEvent('pdf_purchase_failed', ampProps);
}
